/**
 * InvoicesListView
 * Simplified invoice list that receives filters from parent (used by RechercheView)
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  DocumentPlusIcon,
  DocumentTextIcon,
  ClockIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  BuildingLibraryIcon,
  EnvelopeIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { useInvoices, useCancelInvoice, useMarkInvoiceAsPaid } from '../../hooks/useInvoices';
import { useBillingContacts } from '../../hooks/useProducts';
import { downloadInvoicePdf } from '../../api/facturation.api';
import { GenerateSingleInvoiceModal } from '../GenerateInvoice/GenerateSingleInvoiceModal';
import { CreateCustomInvoiceModal } from '../CustomInvoice/CreateCustomInvoiceModal';
import { InvoiceDetailModal } from '../InvoiceDetailModal';
import { TableSkeletonLoader } from '../SkeletonLoader';
import { useAppStore } from '@/app/store';
import type { Invoice, InvoiceStatus, InvoiceFilters, InvoiceSortBy } from '../../types/facturation.types';
import { formatDateShort } from '../../utils/dateFormat';
import '../../facturation.css';

// Status configuration
const STATUS_CONFIG: Record<InvoiceStatus, { label: string; className: string }> = {
  DRAFT: { label: 'Brouillon', className: 'draft' },
  GENERATED: { label: 'Genere', className: 'generated' },
  SENT: { label: 'Envoye', className: 'sent' },
  PAID: { label: 'Paye', className: 'paid' },
  CANCELLED: { label: 'Annule', className: 'cancelled' },
};

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

interface InvoicesListViewProps {
  initialFilters: InvoiceFilters;
  searchLabel?: string;
  searchContext?: {
    mode: 'affaire' | 'client' | 'facture';
    term: string;
  };
}

export const InvoicesListView: React.FC<InvoicesListViewProps> = ({
  initialFilters,
  searchLabel,
  searchContext,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filters from URL search params (or use initialFilters as fallback)
  const getInitialFiltersFromUrl = (): InvoiceFilters => {
    const status = searchParams.get('status') as InvoiceStatus | null;
    const sortBy = searchParams.get('sort_by') as InvoiceSortBy | null;
    const sortOrder = searchParams.get('sort_order') as 'ASC' | 'DESC' | null;
    const page = searchParams.get('page');

    return {
      ...initialFilters,
      ...(status && { status }),
      ...(sortBy && { sort_by: sortBy }),
      ...(sortOrder && { sort_order: sortOrder }),
      ...(page && { page: parseInt(page) }),
    };
  };

  // Filters for list view
  const [filters, setFilters] = useState<InvoiceFilters>(getInitialFiltersFromUrl());

  // Reset filters when initialFilters change
  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Generate invoice modal state
  const [showGenerateMenu, setShowGenerateMenu] = useState(false);
  const [showSingleInvoiceModal, setShowSingleInvoiceModal] = useState(false);
  const [showCustomInvoiceModal, setShowCustomInvoiceModal] = useState(false);
  const [singleInvoiceAffaireId, setSingleInvoiceAffaireId] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const { data: invoicesData, isLoading, refetch } = useInvoices(filters);
  const cancelMutation = useCancelInvoice();
  const markPaidMutation = useMarkInvoiceAsPaid();
  const addNotification = useAppStore((s) => s.addNotification);

  // Fetch billing contacts when in affaire mode (for prefilling custom invoice)
  const affaireId = searchContext?.mode === 'affaire' ? searchContext.term : '';
  const { data: billingContactsResponse } = useBillingContacts(affaireId);
  // Handle both array and ApiResponse formats
  const billingContactsArray = Array.isArray(billingContactsResponse)
    ? billingContactsResponse
    : (billingContactsResponse as any)?.data;
  const primaryBillingContact = billingContactsArray?.[0];

  // Update filters and sync with URL
  const updateFilters = (newFilters: Partial<InvoiceFilters>) => {
    const updated = { ...filters, ...newFilters };
    setFilters(updated);

    // Update URL params
    const params = new URLSearchParams();

    // Only add non-default values to URL
    if (updated.status) params.set('status', updated.status);
    if (updated.sort_by) params.set('sort_by', updated.sort_by);
    if (updated.sort_order && updated.sort_order !== 'DESC') params.set('sort_order', updated.sort_order);
    if (updated.page && updated.page > 1) params.set('page', String(updated.page));

    setSearchParams(params, { replace: true });
  };

  const handleStatusFilter = (status?: InvoiceStatus) => {
    updateFilters({ status, page: 1 });
  };

  const handleSort = (column: InvoiceSortBy) => {
    const newOrder = filters.sort_by === column && filters.sort_order === 'DESC' ? 'ASC' : 'DESC';
    updateFilters({ sort_by: column, sort_order: newOrder, page: 1 });
  };

  const renderSortIcon = (column: InvoiceSortBy) => {
    if (filters.sort_by !== column) {
      return <ChevronUpDownIcon className="w-4 h-4 sort-icon inactive" />;
    }
    return filters.sort_order === 'DESC'
      ? <ChevronDownIcon className="w-4 h-4 sort-icon active" />
      : <ChevronUpIcon className="w-4 h-4 sort-icon active" />;
  };

  const handleDownloadPdf = async (invoice: Invoice, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const blob = await downloadInvoicePdf(invoice.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Handle null invoice_number (draft invoices)
      const fileName = invoice.invoice_number
        ? `facture_${invoice.invoice_number.replace(/\//g, '-')}.pdf`
        : `facture_brouillon_${invoice.id}.pdf`;
      a.download = fileName;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 100); // Delay for download to start
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleCancel = async (id: number, status: InvoiceStatus, e: React.MouseEvent) => {
    e.stopPropagation();
    const reason = prompt('Raison de l\'annulation:');
    if (reason) {
      try {
        await cancelMutation.mutateAsync({ id, reason, currentStatus: status });
        refetch();
      } catch (error) {
        console.error('Cancel failed:', error);
      }
    }
  };

  const handleMarkPaid = async (id: number, status: InvoiceStatus, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Marquer cette facture comme payee ?')) {
      try {
        await markPaidMutation.mutateAsync({ id, currentStatus: status });
        addNotification({ type: 'success', title: 'Facture marquee comme payee' });
        refetch();
      } catch (error) {
        console.error('Mark paid failed:', error);
        addNotification({ type: 'error', title: 'Erreur', message: 'Impossible de marquer la facture comme payee.' });
      }
    }
  };

  const handleOpenSingleInvoice = () => {
    // If we searched by affaire, use that affaire ID directly
    if (searchContext?.mode === 'affaire' && searchContext.term) {
      setSingleInvoiceAffaireId(searchContext.term);
      setShowSingleInvoiceModal(true);
      setShowGenerateMenu(false);
      return;
    }

    // Otherwise, prompt for affaire ID
    const affaireId = prompt('Entrez le numero d\'affaire (ID):');
    if (affaireId) {
      setSingleInvoiceAffaireId(affaireId.trim());
      setShowSingleInvoiceModal(true);
    }
    setShowGenerateMenu(false);
  };

  const handleGenerateSuccess = () => {
    refetch();
  };

  const getAffaireRef = (invoice: Invoice): string => {
    if (invoice.affaires?.length && invoice.affaires.length > 0) {
      return invoice.affaires[0].k_id_dossier || invoice.affaires[0].affaire_id;
    }
    return '-';
  };

  const handleRowClick = async (invoice: Invoice) => {
    // Open the invoice detail modal for all clickable invoices
    if (invoice.status === 'DRAFT' || invoice.pdf_path) {
      setSelectedInvoice(invoice);
    }
  };

  const handleDetailSuccess = () => {
    refetch();
  };

  return (
    <div className="fact-invoices-list">
      {/* Controls: Status Filters + Generate Button */}
      <div className="fact-controls">
        <div className="fact-controls-left">
          <div className="fact-status-filters">
            <button
              className={`fact-status-btn ${!filters.status ? 'active' : ''}`}
              onClick={() => handleStatusFilter(undefined)}
            >
              Tous
            </button>
            <button
              className={`fact-status-btn ${filters.status === 'DRAFT' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('DRAFT')}
            >
              Brouillons
            </button>
            <button
              className={`fact-status-btn ${filters.status === 'GENERATED' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('GENERATED')}
            >
              Generes
            </button>
            <button
              className={`fact-status-btn ${filters.status === 'PAID' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('PAID')}
            >
              Payes
            </button>
          </div>
        </div>

        <div className="fact-controls-right">
          <div className="relative">
            <button
              className="fact-btn fact-btn-primary"
              onClick={() => setShowGenerateMenu(!showGenerateMenu)}
            >
              <PlusIcon className="w-5 h-5" />
              <span>Generer Facture</span>
            </button>

            {showGenerateMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowGenerateMenu(false)}
                />
                <div className="fact-dropdown-menu">
                  <button onClick={handleOpenSingleInvoice} className="fact-dropdown-item">
                    <DocumentPlusIcon className="fact-dropdown-item-icon" />
                    <div className="fact-dropdown-item-content">
                      <span className="fact-dropdown-item-title">Facture simple</span>
                      <span className="fact-dropdown-item-desc">Pour une seule affaire</span>
                    </div>
                  </button>
                  {/* Separator */}
                  <div className="fact-dropdown-separator" />
                  <div className="fact-dropdown-section-title">Documents personnalises</div>
                  <button
                    onClick={() => {
                      setShowCustomInvoiceModal(true);
                      setShowGenerateMenu(false);
                    }}
                    className="fact-dropdown-item"
                  >
                    <DocumentTextIcon className="fact-dropdown-item-icon" />
                    <div className="fact-dropdown-item-content">
                      <span className="fact-dropdown-item-title">Facture personnalisee</span>
                      <span className="fact-dropdown-item-desc">
                        {primaryBillingContact ? 'Pre-remplie avec le contact facturation' : 'Sans lien avec une affaire'}
                      </span>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Invoice List */}
      <div className="fact-list">
        {isLoading ? (
          <TableSkeletonLoader rows={5} columns={[100, 70, 200, 90, 100, 100, 100, 80]} />
        ) : !invoicesData?.invoices || invoicesData.invoices.length === 0 ? (
          <div className="fact-empty">
            <DocumentTextIcon className="w-12 h-12" />
            <h3>Aucune facture</h3>
            <p>Aucune facture trouvee pour cette recherche.</p>
          </div>
        ) : (
          <div className="fact-results-container">
            <div className="fact-table">
              <div className="fact-table-header">
                <div className="sortable" onClick={() => handleSort('invoice_number')}>
                  Numero {renderSortIcon('invoice_number')}
                </div>
                <div className="sortable" onClick={() => handleSort('affaire_id')}>
                  Affaire {renderSortIcon('affaire_id')}
                </div>
                <div className="sortable" onClick={() => handleSort('billing_name')}>
                  Client {renderSortIcon('billing_name')}
                </div>
                <div>
                  Créé par
                </div>
                <div className="sortable" onClick={() => handleSort('invoice_date')}>
                  Date {renderSortIcon('invoice_date')}
                </div>
                <div className="sortable align-right" onClick={() => handleSort('total_tvac')}>
                  Montant {renderSortIcon('total_tvac')}
                </div>
                <div>
                  Paiement
                </div>
                <div className="sortable" onClick={() => handleSort('status')}>
                  Statut {renderSortIcon('status')}
                </div>
                <div style={{ textAlign: 'right' }}>Actions</div>
              </div>

              {invoicesData.invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className={`fact-table-row ${invoice.pdf_path || invoice.status === 'DRAFT' ? 'clickable' : ''}`}
                  onClick={() => handleRowClick(invoice)}
                >
                  <div className="fact-col-invoice-number">
                    {invoice.invoice_number ? (
                      <span className="fact-invoice-number">{invoice.invoice_number}</span>
                    ) : (
                      <span className="fact-invoice-number fact-draft-label">Brouillon</span>
                    )}
                    <span
                      className={`fact-invoice-type-chip fact-invoice-type-chip--${(invoice.invoice_type || 'CUSTOM').toLowerCase()}`}
                      title={
                        invoice.invoice_type === 'MONTHLY'
                          ? 'Facture groupée mensuelle'
                          : invoice.invoice_type === 'SINGLE'
                            ? 'Facture directe (client)'
                            : 'Facture personnalisée'
                      }
                    >
                      {invoice.invoice_type === 'MONTHLY'
                        ? 'Mensuelle'
                        : invoice.invoice_type === 'SINGLE'
                          ? 'Directe'
                          : 'Custom'}
                    </span>
                  </div>
                  <div>
                    <span className="fact-affaire-number" title={getAffaireRef(invoice)}>{getAffaireRef(invoice)}</span>
                  </div>
                  <div className="fact-col-client">
                    <span className="fact-client-name">{invoice.billing_name}</span>
                    {invoice.billing_vat_number && (
                      <span className="fact-client-vat">{invoice.billing_vat_number}</span>
                    )}
                  </div>
                  <div className="fact-col-creator">
                    {invoice.first_name || invoice.last_name ? (
                      <span title={`Créé le ${new Date(invoice.created_at).toLocaleString('fr-BE')}`}>
                        {invoice.first_name || ''} {invoice.last_name || ''}
                      </span>
                    ) : (
                      <span className="fact-no-creator">-</span>
                    )}
                  </div>
                  <div className="fact-col-date">
                    <ClockIcon className="w-4 h-4" />
                    {formatDateShort(invoice.invoice_date)}
                  </div>
                  <div className="fact-col-amount">
                    {formatCurrency(invoice.total_tvac)}
                  </div>
                  <div className="fact-col-payment">
                    {(() => {
                      const received = Number(invoice.total_payment_received) || 0;
                      const total = Number(invoice.total_tvac) || 0;
                      const credited = Number(invoice.total_credited) || 0;
                      const net = total - credited;

                      if (invoice.status === 'CANCELLED' || invoice.status === 'DRAFT') {
                        return <span className="fact-payment-badge neutral">-</span>;
                      }
                      if (net <= 0) {
                        return <span className="fact-payment-badge neutral">N/A</span>;
                      }
                      if (received >= net - 0.01) {
                        return (
                          <span className="fact-payment-badge paid" title={`${formatCurrency(received)} recu`}>
                            Paye
                          </span>
                        );
                      }
                      // Invoice manually marked as PAID but no linked payment records
                      if (invoice.status === 'PAID' && received === 0) {
                        return (
                          <span className="fact-payment-badge paid" title="Marque paye manuellement">
                            Paye
                          </span>
                        );
                      }
                      if (received > 0) {
                        return (
                          <span className="fact-payment-badge partial" title={`${formatCurrency(received)} / ${formatCurrency(net)}`}>
                            {formatCurrency(received)}
                          </span>
                        );
                      }
                      return (
                        <span className="fact-payment-badge unpaid" title="Aucun paiement recu">
                          Impaye
                        </span>
                      );
                    })()}
                  </div>
                  <div className="fact-col-status">
                    <span className={`fact-status-badge ${STATUS_CONFIG[invoice.status].className}`}>
                      {STATUS_CONFIG[invoice.status].label}
                    </span>
                    {/* Delivery status badges - only show for non-draft invoices */}
                    {invoice.status !== 'DRAFT' && (
                      <div className="fact-delivery-badges">
                        {/* Falco badge */}
                        <span
                          className={`fact-delivery-badge ${
                            invoice.falco_status === 'SENT' || invoice.falco_status === 'UPLOADED'
                              ? 'success'
                              : invoice.falco_status === 'FAILED'
                              ? 'error'
                              : invoice.falco_status === 'PENDING' || invoice.falco_status === 'PROCESSING'
                              ? 'pending'
                              : 'inactive'
                          }`}
                          title={`Falco: ${invoice.falco_status || 'Non envoyé'}`}
                        >
                          <BuildingLibraryIcon className="w-3.5 h-3.5" />
                        </span>
                        {/* Customer email badge */}
                        <span
                          className={`fact-delivery-badge ${
                            invoice.email_sent
                              ? 'success'
                              : invoice.email_error
                              ? 'error'
                              : 'inactive'
                          }`}
                          title={
                            invoice.email_sent
                              ? `Email envoyé${invoice.email_recipient ? ` à ${invoice.email_recipient}` : ''}${invoice.email_sent_at ? ` le ${new Date(invoice.email_sent_at).toLocaleDateString('fr-BE')}` : ''}`
                              : invoice.email_error
                              ? `Erreur email: ${invoice.email_error}`
                              : 'Email non envoyé'
                          }
                        >
                          <EnvelopeIcon className="w-3.5 h-3.5" />
                        </span>
                        {/* Peppol badge */}
                        <span
                          className={`fact-delivery-badge ${
                            invoice.peppol_status === 'DELIVERED'
                              ? 'success'
                              : invoice.peppol_status === 'FAILED'
                              ? 'error'
                              : invoice.peppol_status === 'PENDING'
                              ? 'pending'
                              : 'inactive'
                          }`}
                          title={`Peppol: ${
                            invoice.peppol_status === 'NOT_REGISTERED'
                              ? 'Client non inscrit'
                              : invoice.peppol_status || 'N/A'
                          }`}
                        >
                          <GlobeAltIcon className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    )}
                    {/* Validator info - show who validated and when */}
                    {invoice.validated_by && invoice.validated_at && (
                      <span className="fact-validator-info" title={`Validé le ${new Date(invoice.validated_at).toLocaleString('fr-BE')}`}>
                        par {invoice.validator_first_name || ''} {invoice.validator_last_name || ''}
                      </span>
                    )}
                  </div>
                  <div className="fact-col-actions">
                    {invoice.pdf_path && (
                      <>
                        <button
                          onClick={(e) => handleDownloadPdf(invoice, e)}
                          className="fact-action-btn download"
                          title="Telecharger PDF"
                        >
                          <ArrowDownTrayIcon />
                        </button>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const blob = await downloadInvoicePdf(invoice.id);
                              const url = window.URL.createObjectURL(blob);
                              const printWindow = window.open(url, '_blank');
                              if (printWindow) {
                                printWindow.addEventListener('load', () => printWindow.print());
                              }
                            } catch {}
                          }}
                          className="fact-action-btn download"
                          title="Imprimer"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z" />
                          </svg>
                        </button>
                      </>
                    )}
                    {/* Mark as paid - only for SENT (validated) invoices */}
                    {invoice.status === 'SENT' && (
                      <button
                        onClick={(e) => handleMarkPaid(invoice.id, invoice.status, e)}
                        className="fact-action-btn paid"
                        title="Marquer paye"
                      >
                        <CheckCircleIcon />
                      </button>
                    )}
                    {/* Cancel - only for DRAFT/GENERATED (not SENT - already in accounting system) */}
                    {invoice.status !== 'PAID' && invoice.status !== 'CANCELLED' && invoice.status !== 'SENT' && (
                      <button
                        onClick={(e) => handleCancel(invoice.id, invoice.status, e)}
                        className="fact-action-btn cancel"
                        title="Annuler"
                      >
                        <XCircleIcon />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {invoicesData.totalPages > 1 && (
              <div className="fact-pagination">
                <button
                  className="fact-page-btn"
                  disabled={invoicesData.page <= 1}
                  onClick={() => updateFilters({ page: (filters.page || 1) - 1 })}
                >
                  Precedent
                </button>
                <span className="fact-page-info">
                  Page {invoicesData.page} sur {invoicesData.totalPages}
                </span>
                <button
                  className="fact-page-btn"
                  disabled={invoicesData.page >= invoicesData.totalPages}
                  onClick={() => updateFilters({ page: (filters.page || 1) + 1 })}
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <GenerateSingleInvoiceModal
        isOpen={showSingleInvoiceModal}
        onClose={() => {
          setShowSingleInvoiceModal(false);
          setSingleInvoiceAffaireId('');
        }}
        affaireId={singleInvoiceAffaireId}
        onSuccess={handleGenerateSuccess}
      />

      <CreateCustomInvoiceModal
        isOpen={showCustomInvoiceModal}
        onClose={() => setShowCustomInvoiceModal(false)}
        onSuccess={handleGenerateSuccess}
        prefillCustomer={primaryBillingContact ? {
          name: primaryBillingContact.name,
          address: primaryBillingContact.address || '',
          email: primaryBillingContact.email || '',
          vatNumber: primaryBillingContact.vat_number || '',
        } : undefined}
        affaireId={searchContext?.mode === 'affaire' ? searchContext.term : undefined}
      />

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          isOpen={!!selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          onSuccess={handleDetailSuccess}
        />
      )}
    </div>
  );
};

export default InvoicesListView;
